import { useCallback, useEffect, useState } from 'react'
import type { Comment, CommentableType } from '@domain/comment'
import type { HouseholdMember } from '@domain/household'
import { Avatar } from '@ui/avatar/Avatar'
import { Botao } from '@ui/glass/Botao'
import { IconeLapis, IconeLixeira } from '@ui/shell/icones'
import { useMembers } from '@ui/settings/use-members'
import { useReadyUser } from '@ui/auth/auth-context'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * A thread de comentários — *"onde a auditabilidade fica visível para uma
 * pessoa"*, em `Docs/04-Design/Telas/Detalhe da transação.md`.
 *
 * ## Comentário de IA e de sistema tem marca própria e tom mais discreto
 *
 * A nota pede isso, e a razão está em `Docs/02-Dominio/Comment.md`: o caso que
 * motiva a entidade é *"a IA explicando o que fez"*. Um comentário que a máquina
 * escreveu e um que uma pessoa escreveu não podem parecer a mesma coisa — a
 * pessoa precisa saber de quem é a afirmação que está lendo.
 *
 * **Nenhum produtor de `ai` ou `system` existe ainda**, e a marca não é
 * decoração antecipada: `firestore.rules` já nega os dois ao cliente desde a
 * Fase 1, então o dia em que a conciliação de fatura escrever *"conciliada
 * automaticamente com o pagamento de 10/08"* — o exemplo que a própria nota usa
 * —, ele aparece certo sem ninguém mexer na tela. Há caso de teste com os dois.
 *
 * ## Só o próprio comentário se edita e se apaga
 *
 * A regra recusa o resto, e a tela não oferece o que a regra nega: um botão que
 * devolvesse `permission-denied` seria uma promessa quebrada. Quem decide é o
 * `authorId` contra o uid da sessão.
 *
 * ## Texto puro, sempre
 *
 * `content` é texto puro por princípio (o 8), e é renderizado como texto — sem
 * markdown, sem HTML. É isso que impede um comentário de virar vetor de injeção,
 * e é por isso que não há um "renderizador" aqui.
 *
 * ## Dois tratamentos de botão aqui, e a divisa é a do `Botao`
 *
 * *"Ação de TELA usa este componente; controle de LINHA não"* — o próprio
 * `Botao` traça a linha, e ela cai bem no meio desta seção. **Comentar** e
 * **Salvar** são a ação do compositor: uma por vez na tela, e são a pílula do
 * sistema, como o "Adicionar item" do detalhamento logo acima. **Editar** e
 * **apagar** se repetem por comentário — ficam ícone puro e apagado, como o
 * ✕ de cada lembrete.
 *
 * O tom é o secundário, e não o `primario` que o confirmar do detalhamento usa:
 * lá a pílula cheia aparece só enquanto o compositor está aberto, aqui ela
 * ficaria na tela o tempo todo — e com o detalhamento aberto seriam **duas**
 * pílulas cheias disputando a mesma coluna. Discussão no pé de um detalhe não é
 * a ação principal da tela.
 *
 * Esses dois perderam a palavra ao lado, e é o único par da interface que pode:
 * lápis e lixeira não têm desenho concorrente. O nome sobrevive no `aria-label`
 * e no `title`.
 */

const AUTHOR_LABELS: Record<Comment['authorType'], string> = {
  user: '',
  system: 'Sistema',
  ai: 'IA',
}

/**
 * Quem escreveu, para o avatar e para o nome.
 *
 * ## O nome vem dos MEMBROS, e não do uid
 *
 * A versão anterior não mostrava autor nenhum — só a data. Numa casa de duas
 * pessoas isso torna a discussão ilegível: "31/08 14:03" não diz quem disse, e
 * a área inteira lia como uma segunda observação da transação em vez de uma
 * conversa. Era essa a semelhança que incomodava.
 *
 * ## O nome vem do DOCUMENTO DE MEMBRO, e não da sessão
 *
 * Os dois existem e são diferentes de propósito — o do membro é o congelado no
 * primeiro login. A sessão só tem o nome de **quem está lendo**, e a discussão
 * precisa nomear os dois lados; usar a sessão para um e o documento para o
 * outro faria o mesmo comentário mudar de autor conforme quem abre a tela.
 *
 * ## Sem nome, o e-mail; sem membro, um genérico
 *
 * Um comentário pode ser de alguém que saiu da casa, ou de um membro que nunca
 * completou o perfil. Mostrar o uid cru seria pior que não mostrar nada — e
 * "alguém da casa" é honesto sobre o que se sabe. Mesma escolha de `labelDe`
 * no ponto de preço.
 */
function autorDe(
  comment: Comment,
  membros: HouseholdMember[],
): { nome: string; foto: string | null } {
  if (comment.authorType !== 'user') {
    return { nome: AUTHOR_LABELS[comment.authorType], foto: null }
  }
  const membro = membros.find((m) => m.userId === comment.authorId) ?? null
  const nome = membro?.displayName?.trim()
  if (nome !== undefined && nome !== '') return { nome, foto: membro?.photoURL ?? null }
  const email = membro?.email.trim()
  return {
    nome: email !== undefined && email !== '' ? email : 'alguém da casa',
    foto: membro?.photoURL ?? null,
  }
}

/** `'2026-08-31T14:03:00.000Z'` → `'31/08 14:03'`. Ano só quando difere. */
function quando(iso: string, anoCorrente: string): string {
  const dia = iso.slice(8, 10)
  const mes = iso.slice(5, 7)
  const hora = iso.slice(11, 16)
  const ano = iso.slice(0, 4)
  return ano === anoCorrente ? `${dia}/${mes} ${hora}` : `${dia}/${mes}/${ano} ${hora}`
}

function Linha({
  comment,
  meu,
  membros,
  anoCorrente,
  onEdit,
  onRemove,
}: {
  comment: Comment
  meu: boolean
  membros: HouseholdMember[]
  anoCorrente: string
  onEdit: (content: string) => Promise<void>
  onRemove: () => Promise<void>
}) {
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState(comment.content)
  const daMaquina = comment.authorType !== 'user'
  const autor = autorDe(comment, membros)

  return (
    <li className="flex gap-2 py-2 text-sm">
      {/* O avatar à esquerda, e o corpo numa bolha à direita — a forma que
          qualquer pessoa reconhece como "alguém falando". Sem ele, uma
          discussão de duas pessoas vira uma pilha de datas. */}
      {daMaquina ? (
        <span
          aria-hidden="true"
          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--surface-sunken)] text-xs text-[var(--text-muted)]"
        >
          {comment.authorType === 'ai' ? '✦' : '⚙'}
        </span>
      ) : (
        <span className="mt-0.5">
          <Avatar displayName={autor.nome} photoURL={autor.foto} size={32} />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div
          className="rounded-2xl px-3 py-2"
          // A bolha tem fundo, e é o `surface-sunken`: um degrau ABAIXO do
          // cartão que a contém. Um degrau acima competiria com o cartão da
          // transação; abaixo, ela lê como algo colado por dentro dele.
          style={{ background: 'var(--surface-sunken)' }}
        >
          <p className="mb-0.5 flex flex-wrap items-baseline gap-x-2">
            <span className="text-xs font-semibold">{autor.nome}</span>
            <span className="text-xs text-[var(--text-muted)]">
              {quando(comment.createdAt, anoCorrente)}
              {comment.editedAt === null ? '' : ' · editado'}
            </span>
          </p>

          {editando ? (
            <>
              <label>
                <span className="sr-only">Editar comentário</span>
                <textarea
                  rows={2}
                  value={texto}
                  className="mt-1 w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
                  onChange={(event) => {
                    setTexto(event.target.value)
                  }}
                />
              </label>
              <div className="mt-2">
                <Botao
                  type="button"
                  onClick={() => {
                    const limpo = texto.trim()
                    if (limpo === '' || limpo === comment.content) {
                      setEditando(false)
                      return
                    }
                    void onEdit(limpo).then(() => {
                      setEditando(false)
                    })
                  }}
                >
                  Salvar
                </Botao>
              </div>
            </>
          ) : (
            // Texto puro, renderizado como texto. `whitespace-pre-wrap` preserva as
            // quebras de linha que a pessoa digitou sem interpretar nada.
            <p className="whitespace-pre-wrap">{comment.content}</p>
          )}

          {meu && !editando && (
            // O recuo de 10px alinha o primeiro ícone com o texto acima: o
            // `alvo` é uma caixa de 44px em volta de um desenho de 24, e sem
            // isso a sobra centrada empurraria o lápis para dentro da bolha.
            <p className="fileira-de-icones -ml-2.5 text-[var(--text-muted)]">
              <button
                type="button"
                className="alvo"
                aria-label="Editar"
                title="Editar"
                onClick={() => {
                  setEditando(true)
                }}
              >
                <IconeLapis />
              </button>
              <button
                type="button"
                className="alvo"
                aria-label="Apagar"
                title="Apagar"
                onClick={() => {
                  void onRemove()
                }}
              >
                <IconeLixeira />
              </button>
            </p>
          )}
        </div>
      </div>
    </li>
  )
}

export function CommentThread({
  entityType,
  entityId,
  anoCorrente,
}: {
  entityType: CommentableType
  entityId: string
  /** Injetado: o relógio do app mora em `shared/date` (decisão 79). */
  anoCorrente: string
}) {
  const { commentRepository } = useFinanceRepositories()
  const user = useReadyUser()
  // Os membros, para o avatar e o nome de quem escreveu. Se a leitura falhar, a
  // discussão continua legível: `autorDe` cai no genérico em vez de sumir.
  const membrosState = useMembers()
  const membros = membrosState.status === 'loaded' ? membrosState.members : []
  const [comments, setComments] = useState<Comment[] | 'loading' | 'failed'>('loading')
  const [novo, setNovo] = useState('')
  const [falhou, setFalhou] = useState(false)

  const recarregar = useCallback(async () => {
    // **Relê depois de escrever**, ao contrário do breakdown e da lista de
    // transações. Aqui o servidor decide o `createdAt`, e é ele que ordena a
    // thread — montar o comentário localmente daria uma ordem que a próxima
    // leitura poderia desmentir. Uma thread são poucos documentos, e o
    // round-trip é o preço de a ordem ser a de verdade desde o primeiro quadro.
    setComments(await commentRepository.listFor(entityType, entityId))
  }, [commentRepository, entityType, entityId])

  useEffect(() => {
    let live = true
    void commentRepository
      .listFor(entityType, entityId)
      .then((lidos) => {
        if (live) setComments(lidos)
      })
      .catch((error: unknown) => {
        console.error('CommentThread: a leitura dos comentários falhou', error)
        if (live) setComments('failed')
      })
    return () => {
      live = false
    }
  }, [commentRepository, entityType, entityId])

  if (comments === 'loading') {
    return (
      <p role="status" className="mt-4 text-sm text-[var(--text-muted)]">
        Carregando comentários…
      </p>
    )
  }
  if (comments === 'failed') {
    return (
      <p role="alert" className="mt-4 text-sm text-[var(--text-muted)]">
        Não foi possível carregar os comentários agora.
      </p>
    )
  }

  /**
   * Toda escrita passa por aqui: grava, relê, e a falha vira um aviso.
   *
   * **As três passavam? Não — e foi um bug.** A primeira versão só mandava o
   * `create` por este caminho; `edit` e `remove` chamavam a porta direto, então
   * a gravação acontecia e **a thread não mudava**. A pessoa apagava um
   * comentário e ele continuava na tela até recarregar a página. Os dois testes
   * que pegaram foram os que afirmam o resultado *na tela*, não a chamada da
   * porta — a chamada estava certa nos dois casos.
   *
   * Devolve uma promessa que **não rejeita**, para quem chama poder encadear
   * sem deixar rejeição solta.
   */
  const gravar = (acao: () => Promise<unknown>): Promise<void> => {
    setFalhou(false)
    return acao()
      .then(recarregar)
      .catch(() => {
        setFalhou(true)
      })
  }

  return (
    <section aria-label="Discussões" className="mt-4 border-t border-[var(--border)] pt-3">
      {/* **"Discussões", e não "Comentários".** A observação da transação, logo
          acima, também é um texto que alguém escreve — e com os dois chamados de
          "comentário" a tela tinha duas caixas de texto dizendo a mesma coisa.
          A diferença é real: a observação é uma nota SOBRE a transação, de quem
          a classifica; isto é uma conversa ENTRE as duas pessoas da casa. */}
      <h2 className="mb-2 text-xs text-[var(--text-muted)]">
        {comments.length === 0 ? 'Discussões' : `Discussões (${String(comments.length)})`}
      </h2>

      <ul>
        {comments.map((comment) => (
          <Linha
            key={comment.id}
            comment={comment}
            membros={membros}
            // Comentário de IA e de sistema não é editável por ninguém — a
            // regra nega, e a tela não oferece o que a regra nega.
            meu={comment.authorType === 'user' && comment.authorId === user.uid}
            anoCorrente={anoCorrente}
            onEdit={(content) => gravar(() => commentRepository.edit(comment.id, content))}
            onRemove={() => gravar(() => commentRepository.remove(comment.id))}
          />
        ))}
      </ul>

      {/* O compositor tem o MEU avatar à esquerda, alinhado com os de cima:
          é o que faz a caixa ler como "a minha vez de falar" em vez de mais um
          campo do formulário da transação. */}
      <div className="mt-2 flex items-start gap-2">
        <span className="mt-0.5">
          <Avatar displayName={user.displayName} photoURL={user.photoURL} size={32} />
        </span>
        <div className="min-w-0 flex-1">
          <label>
            <span className="sr-only">Novo comentário</span>
            <textarea
              rows={2}
              value={novo}
              placeholder="Escrever na discussão"
              className="w-full rounded-2xl border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
              onChange={(event) => {
                setNovo(event.target.value)
              }}
            />
          </label>
          <div className="mt-2">
            <Botao
              type="button"
              onClick={() => {
                const limpo = novo.trim()
                // Comentário vazio não é comentário. Sem isto, tocar no botão
                // sem digitar criaria uma linha em branco na thread — que
                // ninguém consegue distinguir de um bug.
                if (limpo === '') return
                void gravar(() =>
                  commentRepository.create(entityType, entityId, limpo).then(() => {
                    setNovo('')
                  }),
                )
              }}
            >
              Comentar
            </Botao>
          </div>
        </div>
      </div>

      {falhou && (
        <p role="alert" className="mt-2 text-sm text-[var(--text-muted)]">
          Não foi possível salvar o comentário agora.
        </p>
      )}
    </section>
  )
}
